/**
 * "إضافة ساعات إضافية" — a port of the legacy extra-hours modal
 * (`openExtraHoursModal` / `saveExtraHours`, doctor_dashboard.js:12167–12800).
 *
 * The doctor picks a clinic, a window, and a slot length; the slots are
 * generated from those and appended to that day after the clinic's normal
 * hours. Optionally the whole block carries a night discount, which the day
 * modal then shows on each extra slot.
 *
 * The night discount rides on the block itself (`hasNightOffer` /
 * `nightOfferDiscount`) rather than spawning a separate offer record, which is
 * what the legacy did by pushing into `doctor.bookingOffers`. One row means the
 * discount cannot outlive the evening it belongs to — deleting the block takes
 * the discount with it, where the legacy left an orphaned offer behind.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Field,
  Icon,
  Input,
  Modal,
  Select,
} from '../../../components/common';
import { generateSlots, clinicSlotsForDate } from '../../../domain/slots';
import { formatTime12 } from '../../../domain/dates';

const DEFAULTS = { startTime: '20:00', endTime: '23:00', duration: 30 };

const dayLabel = (dateKey) => {
  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateKey;
  return date.toLocaleDateString('ar-EG', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export function ExtraHoursModal({ open, dateKey, clinics = [], existing = [], onClose, onSave }) {
  const [clinicId, setClinicId] = useState('');
  const [form, setForm] = useState(DEFAULTS);
  const [hasNightOffer, setHasNightOffer] = useState(false);
  const [nightOfferDiscount, setNightOfferDiscount] = useState(20);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const clinic = useMemo(
    () => clinics.find((c) => String(c.id) === String(clinicId)) || clinics[0] || null,
    [clinics, clinicId],
  );

  const entry = useMemo(
    () =>
      existing.find(
        (e) => e.date === dateKey && String(e.clinicId) === String(clinic?.id),
      ) || null,
    [existing, dateKey, clinic],
  );

  // Reopening the modal on a different day (or clinic) must show that day's
  // block, not whatever was typed last time.
  useEffect(() => {
    if (!open) return;
    setError('');
    if (entry?.slots?.length) {
      setForm({
        startTime: entry.slots[0],
        endTime: entry.endTime || DEFAULTS.endTime,
        duration: entry.duration || DEFAULTS.duration,
      });
      setHasNightOffer(Boolean(entry.hasNightOffer));
      setNightOfferDiscount(entry.nightOfferDiscount || 20);
    } else {
      setForm(DEFAULTS);
      setHasNightOffer(false);
      setNightOfferDiscount(20);
    }
  }, [open, entry]);

  useEffect(() => {
    if (open && !clinicId && clinics.length) setClinicId(String(clinics[0].id));
  }, [open, clinicId, clinics]);

  const preview = useMemo(
    () => generateSlots(form.startTime, form.endTime, Number(form.duration)),
    [form],
  );

  // Extra hours sit outside the clinic's normal window; overlapping slots would
  // render twice in the day modal and could be double-booked.
  const clash = useMemo(() => {
    if (!clinic) return [];
    const regular = new Set(clinicSlotsForDate(clinic, dateKey));
    return preview.filter((time) => regular.has(time));
  }, [preview, clinic, dateKey]);

  if (!open) return null;

  const submit = async () => {
    if (!clinic) return setError('اختر عيادة أولاً');
    if (preview.length === 0) {
      return setError('لا توجد مواعيد في هذه الفترة — تأكد من وقت البداية والنهاية ومدة الكشف.');
    }
    if (clash.length > 0) {
      return setError(
        `${clash.length} موعد يتعارض مع مواعيد العيادة العادية (${clash
          .slice(0, 3)
          .map(formatTime12)
          .join('، ')}). اختر فترة بعد انتهاء الدوام.`,
      );
    }
    setSaving(true);
    try {
      await onSave({
        date: dateKey,
        clinicId: clinic.id,
        slots: preview,
        endTime: form.endTime,
        duration: Number(form.duration),
        hasNightOffer,
        nightOfferDiscount: Number(nightOfferDiscount) || 0,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await onSave({ date: dateKey, clinicId: clinic.id, slots: [] });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="ساعات إضافية"
      icon="clock"
      size="md"
      footer={
        <>
          {entry && (
            <Button variant="danger" icon="trash-2" onClick={remove} loading={saving}>
              إزالة الساعات الإضافية
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            إلغاء
          </Button>
          <Button icon="check" onClick={submit} loading={saving}>
            حفظ
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-slate-400">{dayLabel(dateKey)}</p>

        {clinics.length > 1 && (
          <Field label="العيادة">
            <Select value={clinic?.id ?? ''} onChange={(e) => setClinicId(e.target.value)}>
              {clinics.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <div className="grid grid-cols-3 gap-3">
          <Field label="من">
            <Input
              type="time"
              value={form.startTime}
              onChange={(e) => setForm((f) => ({ ...f, startTime: e.target.value }))}
            />
          </Field>
          <Field label="إلى">
            <Input
              type="time"
              value={form.endTime}
              onChange={(e) => setForm((f) => ({ ...f, endTime: e.target.value }))}
            />
          </Field>
          <Field label="مدة الكشف (دقيقة)">
            <Input
              type="number"
              min={5}
              step={5}
              value={form.duration}
              onChange={(e) => setForm((f) => ({ ...f, duration: e.target.value }))}
            />
          </Field>
        </div>

        <div className="rounded-xl border border-white/10 bg-white/5 p-3">
          <Checkbox
            label="عرض ليلي على هذه المواعيد 🌙"
            checked={hasNightOffer}
            onChange={(e) => setHasNightOffer(e.target.checked)}
          />
          {hasNightOffer && (
            <div className="mt-3">
              <Field label="نسبة الخصم %">
                <Input
                  type="number"
                  min={1}
                  max={90}
                  value={nightOfferDiscount}
                  onChange={(e) => setNightOfferDiscount(e.target.value)}
                  className="w-32"
                />
              </Field>
            </div>
          )}
        </div>

        <div>
          <p className="mb-2 text-xs text-slate-400">
            {preview.length > 0
              ? `${preview.length} موعد إضافي`
              : 'لا توجد مواعيد في هذه الفترة'}
          </p>
          {preview.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {preview.map((time) => (
                <span
                  key={time}
                  className="rounded-lg px-2 py-1 text-[11px] font-bold"
                  style={{
                    background: '#2d1f00',
                    border: '1px solid #7d5a00',
                    color: '#fde047',
                  }}
                >
                  {formatTime12(time)}
                </span>
              ))}
            </div>
          )}
        </div>

        {error && (
          <p className="flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-300">
            <Icon name="alert-circle" size={14} className="mt-px shrink-0" />
            {error}
          </p>
        )}

        <p className="text-[11px] leading-relaxed text-slate-500">
          تظهر هذه المواعيد للمرضى في تقويم الحجز فور الحفظ.
        </p>
      </div>
    </Modal>
  );
}

export default ExtraHoursModal;
